import { Box, Flex, Text, Icon } from "@chakra-ui/react";
import RosaMobilePreview from "./RosaMobilePreview";
import {
  FaInbox,
  FaBolt,
  FaUser,
  FaChartBar,
  FaBook,
  FaSearch,
  FaEnvelope,
  FaPhone,
  FaBriefcase,
  FaPaperPlane,
  FaCog,
  FaMoon,
  FaChevronLeft,
  FaChevronRight,
  FaChevronDown,
  FaPaperclip,
} from "react-icons/fa";

// A static, non-interactive recreation of the real Rosa support inbox (see
// the screenshot this was built from) — used only as a homepage teaser
// image, the same role BobPreviewCard plays for BOB: a flat card, not a
// device frame, matching how the other product previews present. Contact
// details are rendered blurred, the way a real product screenshot would
// mask customer PII rather than show plausible-looking fake data in the
// clear.

const NAV = [
  { label: "Beérkező levelek", icon: FaInbox, active: true },
  { label: "AI várólista", icon: FaBolt, badge: "5" },
  { label: "Hozzám rendelve", icon: FaUser, badge: "9" },
  { label: "Statisztika", icon: FaChartBar },
  { label: "Tudásbázis", icon: FaBook },
];

const FILTERS = [
  { label: "Nyitott", tone: "neutral" },
  { label: "Emberre vár", tone: "amber" },
  { label: "Megoldva", tone: "green" },
];

const STATUS_STYLES: Record<string, { bg: string; color: string }> = {
  "Emberre vár": { bg: "#FEF3C7", color: "#92400E" },
  "Megoldva": { bg: "#D1FAE5", color: "#065F46" },
  "Ügyfélre vár": { bg: "#DBEAFE", color: "#1E40AF" },
};

const TICKETS = [
  { name: "Lindsay Lueilwitz", initials: "LL", time: "3 órája", subject: "Nem generálódnak a számlák", status: "Emberre vár", swatch: "#8B5CF6", active: true },
  { name: "Robin Abshire", initials: "RA", time: "4 órája", subject: "Nem sikerül a regisztráció", status: "Emberre vár", swatch: "#F59E0B" },
  { name: "Edward Monahan", initials: "EM", time: "9 órája", subject: "Szolgáltatás időtartamának beállítása", status: "Megoldva", swatch: "#EC4899" },
  { name: "Jody Dibbert", initials: "JD", time: "11 órája", subject: "Online jelenlét fejlesztése", status: "Ügyfélre vár", swatch: "#10B981" },
];

const SUGGESTIONS = [
  "Számlázz a Wobble rendszerén keresztül",
  "Előlegfizetés Stripe segítségével",
];

// A blurred placeholder bar standing in for a real, sensitive value
// (email, phone) — same width/height rhythm as real text, but never
// legible, matching how a real screenshot would redact customer data.
const BlurredField = ({ w }: { w: string }) => (
  <Box h="9px" w={w} borderRadius="full" bg="slate.400" filter="blur(3px)" userSelect="none" aria-hidden />
);

const SectionLabel = ({ children }: { children: string }) => (
  <Text fontSize="8px" fontWeight="700" letterSpacing="0.05em" color="slate.400" textTransform="uppercase" mb={2}>
    {children}
  </Text>
);

const RosaPreviewCard = () => (
  <Box
    borderRadius="2xl"
    overflow="hidden"
    border="1px solid"
    borderColor="chalk.400"
    boxShadow="0 24px 70px rgba(76, 29, 149, 0.16)"
    bg="white"
  >
    {/* Below "lg" this card doesn't have room for four real desktop
        columns at a legible size, so instead of shrinking them (or relying
        on horizontal scroll) it swaps in RosaMobilePreview — an actual
        mobile-shaped ticket-list view, the same move WobbleShowcase makes
        by showing WobbleBookingMock's phone mock instead of a squeezed
        admin dashboard on small screens. */}
    <Box display={{ base: "block", lg: "none" }}>
      <RosaMobilePreview />
    </Box>

    <Box display={{ base: "none", lg: "block" }} overflowX="auto">
      <Flex align="stretch" minW="900px">
        {/* Sidebar */}
      <Flex
        direction="column"
        justify="space-between"
        w={{ base: "56px", sm: "140px" }}
        flexShrink={0}
        bg="white"
        borderRight="1px solid"
        borderColor="chalk.300"
        px={{ base: 1.5, sm: 3.5 }}
        py={4}
      >
        <Box>
          <Flex align="center" gap={2} mb={5}>
            <Flex
              w="26px"
              h="26px"
              borderRadius="full"
              bgGradient="linear(to-br, bob.300, rosa.600)"
              color="white"
              align="center"
              justify="center"
              fontSize="11px"
              fontWeight="800"
              flexShrink={0}
            >
              R
            </Flex>
            <Text display={{ base: "none", sm: "block" }} fontSize="xs" fontWeight="800" color="ink.900" noOfLines={1}>
              Rosa
            </Text>
          </Flex>

          <Flex direction="column" gap={1}>
            {NAV.map((item) => (
              <Flex
                key={item.label}
                align="center"
                justify={{ base: "center", sm: "space-between" }}
                gap={2}
                px={{ base: 0, sm: 2.5 }}
                py={1.5}
                borderRadius="md"
                fontSize="10px"
                fontWeight={item.active ? "700" : "500"}
                color={item.active ? "rosa.600" : "slate.500"}
                bg={item.active ? "rosa.50" : "transparent"}
              >
                <Flex align="center" gap={2}>
                  <Icon as={item.icon} boxSize="10px" flexShrink={0} />
                  <Text noOfLines={1} display={{ base: "none", sm: "block" }}>
                    {item.label}
                  </Text>
                </Flex>
                {item.badge && (
                  <Flex
                    display={{ base: "none", sm: "flex" }}
                    w="15px"
                    h="15px"
                    borderRadius="full"
                    bg="rosa.100"
                    color="rosa.700"
                    align="center"
                    justify="center"
                    fontSize="8px"
                    fontWeight="700"
                    flexShrink={0}
                  >
                    {item.badge}
                  </Flex>
                )}
              </Flex>
            ))}
          </Flex>
        </Box>

        {/* User profile footer, matching the reference's bottom-of-sidebar block */}
        <Box display={{ base: "none", sm: "block" }} pt={3} borderTop="1px solid" borderColor="chalk.300">
          <Flex align="center" justify="space-between" mb={2}>
            <Flex align="center" gap={2} minW={0}>
              <Flex
                w="22px"
                h="22px"
                borderRadius="full"
                bg="#10B981"
                color="white"
                align="center"
                justify="center"
                fontSize="9px"
                fontWeight="700"
                flexShrink={0}
              >
                AK
              </Flex>
              <Box minW={0}>
                <Text fontSize="10px" fontWeight="700" color="ink.900" noOfLines={1}>
                  Anna Kovács
                </Text>
                <Text fontSize="7px" fontWeight="700" letterSpacing="0.04em" color="slate.400" textTransform="uppercase">
                  Supervisor
                </Text>
              </Box>
            </Flex>
            {/* Decorative toggle shape, not a real Switch — this is a static
                mockup, and a real <Switch> would render an interactive
                pointer cursor for a control that does nothing. */}
            <Flex w="26px" h="15px" borderRadius="full" bg="#10B981" align="center" px="2px" flexShrink={0}>
              <Box w="11px" h="11px" borderRadius="full" bg="white" ml="auto" />
            </Flex>
          </Flex>
          <Flex align="center" gap={1.5} color="slate.400">
            <Icon as={FaMoon} boxSize="8px" />
            <Text fontSize="9px">Light mode</Text>
          </Flex>
        </Box>
      </Flex>

      {/* Ticket list */}
      <Flex direction="column" w={{ base: "150px", sm: "230px" }} flexShrink={0} borderRight="1px solid" borderColor="chalk.300">
        <Box px={{ base: 2.5, sm: 4 }} pt={3.5} pb={2.5} borderBottom="1px solid" borderColor="chalk.300">
          <Text fontSize={{ base: "xs", sm: "sm" }} fontWeight="800" color="ink.900" mb={0.5}>
            Beérkező levelek
          </Text>
          <Text fontSize="8px" color="slate.500" mb={2.5} display={{ base: "none", sm: "block" }}>
            Minden ügyfélmegkeresés egy helyen.
          </Text>
          <Flex align="center" gap={1.5} bg="chalk.100" borderRadius="md" px={2} py={1.5} mb={2}>
            <Icon as={FaSearch} boxSize="8px" color="slate.400" />
            <Text fontSize="9px" color="slate.400" display={{ base: "none", sm: "block" }}>
              Keresés...
            </Text>
          </Flex>
          <Flex gap={1} wrap="wrap" display={{ base: "none", sm: "flex" }}>
            {FILTERS.map((f) => (
              <Box
                key={f.label}
                fontSize="7px"
                fontWeight="700"
                px={1.5}
                py={0.5}
                borderRadius="full"
                border="1px solid"
                borderColor={f.tone === "neutral" ? "chalk.400" : "transparent"}
                bg={f.tone === "amber" ? "#FEF3C7" : f.tone === "green" ? "#D1FAE5" : "white"}
                color={f.tone === "amber" ? "#92400E" : f.tone === "green" ? "#065F46" : "slate.500"}
              >
                {f.label}
              </Box>
            ))}
          </Flex>
        </Box>

        <Flex direction="column" flex="1">
          {TICKETS.map((t) => {
            const style = STATUS_STYLES[t.status];
            return (
              <Flex
                key={t.name}
                align="flex-start"
                gap={2}
                px={{ base: 2.5, sm: 4 }}
                py={2.5}
                bg={t.active ? "rosa.50" : "transparent"}
                borderLeft="2px solid"
                borderColor={t.active ? "rosa.500" : "transparent"}
                borderBottom="1px solid"
                borderBottomColor="chalk.200"
              >
                <Flex
                  w="18px"
                  h="18px"
                  borderRadius="full"
                  bg={t.swatch}
                  color="white"
                  align="center"
                  justify="center"
                  fontSize="8px"
                  fontWeight="700"
                  flexShrink={0}
                  mt={0.5}
                >
                  {t.initials}
                </Flex>
                <Box flex="1" minW={0} display={{ base: "none", sm: "block" }}>
                  <Flex justify="space-between" align="baseline" gap={1}>
                    <Text fontSize="10px" fontWeight="700" color="ink.900" noOfLines={1}>
                      {t.name}
                    </Text>
                    <Text fontSize="7px" color="slate.400" flexShrink={0}>
                      {t.time}
                    </Text>
                  </Flex>
                  <Text fontSize="9px" color="slate.500" noOfLines={1} mb={1.5}>
                    {t.subject}
                  </Text>
                  <Box
                    display="inline-block"
                    fontSize="7px"
                    fontWeight="700"
                    px={1.5}
                    py={0.5}
                    borderRadius="full"
                    bg={style.bg}
                    color={style.color}
                  >
                    {t.status}
                  </Box>
                </Box>
              </Flex>
            );
          })}
        </Flex>

        <Flex
          align="center"
          justify="space-between"
          px={{ base: 2.5, sm: 4 }}
          py={2}
          borderTop="1px solid"
          borderColor="chalk.300"
          display={{ base: "none", sm: "flex" }}
        >
          <Text fontSize="8px" color="slate.400">
            1–25 / 78
          </Text>
          <Flex gap={1}>
            <Icon as={FaChevronLeft} boxSize="7px" color="slate.400" />
            <Icon as={FaChevronRight} boxSize="7px" color="slate.400" />
          </Flex>
        </Flex>
      </Flex>

      {/* Conversation */}
      <Flex direction="column" flex="1.2" minW={{ base: "180px", sm: "260px" }}>
        <Flex justify="space-between" align="center" px={{ base: 3, sm: 5 }} py={3} borderBottom="1px solid" borderColor="chalk.300" gap={2}>
          <Box minW={0}>
            <Text fontSize={{ base: "11px", sm: "sm" }} fontWeight="800" color="ink.900" noOfLines={1}>
              Nem generálódnak a számlák
            </Text>
            <Text fontSize="8px" color="slate.500" noOfLines={1} display={{ base: "none", md: "block" }}>
              Lindsay Lueilwitz · 2 üzenet
            </Text>
          </Box>
          <Flex gap={1.5} flexShrink={0} align="center">
            <Flex
              align="center"
              gap={1}
              display={{ base: "none", md: "flex" }}
              border="1px solid"
              borderColor="chalk.400"
              borderRadius="full"
              pl={1}
              pr={2}
              py={0.5}
              flexShrink={0}
            >
              <Flex w="14px" h="14px" borderRadius="full" bg="#10B981" color="white" align="center" justify="center" fontSize="6px" fontWeight="700">
                AK
              </Flex>
              <Text fontSize="8px" fontWeight="600" color="ink.900" whiteSpace="nowrap">
                Anna Kovács
              </Text>
              <Icon as={FaChevronDown} boxSize="6px" color="slate.400" />
            </Flex>
            <Flex align="center" gap={1} fontSize="8px" fontWeight="700" px={2} py={1} borderRadius="full" bg="#FEF3C7" color="#92400E" flexShrink={0} whiteSpace="nowrap">
              Emberre vár
              <Icon as={FaChevronDown} boxSize="6px" />
            </Flex>
          </Flex>
        </Flex>

        <Box px={{ base: 3, sm: 5 }} py={4} flex="1">
          <Box mb={4} p={3} borderRadius="lg" bg="chalk.100">
            <Text fontSize="9px" color="slate.600" lineHeight="1.7" mb={1.5}>
              Előző cikkünkben megmutattuk, hogyan tudod összekapcsolni a Stripe fiókodat, vagy létrehozni sajátot, és összekapcsolni a Wobble rendszerrel, hogy előleget tudj beállítani.
            </Text>
            <Text fontSize="9px" color="slate.600" lineHeight="1.7">
              Üdv, Rosa AI
            </Text>
          </Box>

          <Flex justify="space-between" align="center" mb={1.5}>
            <Text fontSize="9px" fontWeight="700" color="slate.500">
              AI válasz biztonsága
            </Text>
            <Text fontSize="9px" fontWeight="700" color="rosa.600">
              94%
            </Text>
          </Flex>
          <Box h="4px" borderRadius="full" bg="chalk.300" overflow="hidden" mb={3}>
            <Box w="94%" h="100%" bg="rosa.500" />
          </Box>

          <Flex gap={1.5} wrap="wrap" mb={4} display={{ base: "none", sm: "flex" }}>
            {SUGGESTIONS.map((s) => (
              <Box key={s} fontSize="8px" fontWeight="600" px={2} py={1} borderRadius="full" bg="rosa.100" color="rosa.700" noOfLines={1}>
                {s}
              </Box>
            ))}
          </Flex>

          <Box border="1px solid" borderColor="rosa.200" borderRadius="lg" px={3} py={2} mb={1.5}>
            <Text fontSize="9px" color="slate.400">
              Válasz írása az ügyfélnek...
            </Text>
          </Box>
          <Flex justify="space-between" align="center">
            <Text fontSize="7px" color="slate.400" display={{ base: "none", sm: "block" }}>
              ⌘/Ctrl + Enter a küldéshez
            </Text>
            <Flex gap={1.5} ml="auto">
              <Flex
                align="center"
                gap={1}
                fontSize="8px"
                fontWeight="600"
                px={2}
                py={1}
                borderRadius="full"
                border="1px solid"
                borderColor="chalk.400"
                color="slate.600"
                display={{ base: "none", sm: "flex" }}
              >
                <Icon as={FaCog} boxSize="7px" />
                AI válasz generálása
              </Flex>
              <Flex align="center" gap={1} fontSize="8px" fontWeight="700" px={2.5} py={1} borderRadius="full" bg="rosa.500" color="white">
                <Icon as={FaPaperPlane} boxSize="7px" />
                Küldés
              </Flex>
            </Flex>
          </Flex>
        </Box>
      </Flex>

      {/* Customer panel */}
      <Flex direction="column" w="190px" flexShrink={0} borderLeft="1px solid" borderColor="chalk.300" px={4} py={4} display={{ base: "none", sm: "flex" }}>
        <SectionLabel>Ügyfél</SectionLabel>
        <Flex align="center" gap={2.5} mb={3}>
          <Flex w="30px" h="30px" borderRadius="full" bg="rosa.500" color="white" align="center" justify="center" fontSize="12px" fontWeight="700" flexShrink={0}>
            LL
          </Flex>
          <Box minW={0}>
            <Text fontSize="10px" fontWeight="700" color="ink.900" noOfLines={1}>
              Lindsay Lueilwitz
            </Text>
            <Text fontSize="8px" color="slate.500" noOfLines={1}>
              Waters Driving School
            </Text>
          </Box>
        </Flex>

        <Flex direction="column" gap={2} mb={4}>
          <Flex align="center" gap={2}>
            <Icon as={FaEnvelope} boxSize="8px" color="slate.400" flexShrink={0} />
            <BlurredField w="80px" />
          </Flex>
          <Flex align="center" gap={2}>
            <Icon as={FaPhone} boxSize="8px" color="slate.400" flexShrink={0} />
            <BlurredField w="64px" />
          </Flex>
          <Flex align="center" gap={2}>
            <Icon as={FaBriefcase} boxSize="8px" color="slate.400" flexShrink={0} />
            <Text fontSize="9px" color="slate.600" noOfLines={1}>
              2 ticket összesen
            </Text>
          </Flex>
        </Flex>

        <SectionLabel>Csatolmányok</SectionLabel>
        <Flex align="center" gap={2} mb={4} p={2} borderRadius="md" bg="chalk.100">
          <Icon as={FaPaperclip} boxSize="8px" color="slate.400" flexShrink={0} />
          <Box minW={0}>
            <Text fontSize="8px" fontWeight="600" color="ink.900" noOfLines={1}>
              szamla-hiba.png
            </Text>
            <Text fontSize="7px" color="slate.400">
              209 KB
            </Text>
          </Box>
        </Flex>

        <SectionLabel>Belső jegyzet</SectionLabel>
        <Box p={2.5} borderRadius="md" bg="#FEFCE8" border="1px solid" borderColor="#FEF08A">
          <Text fontSize="8px" color="#713F12" lineHeight="1.6">
            Megerősítve a számlázási szolgáltatóval, hogy az integráció rendben van.
          </Text>
        </Box>
      </Flex>
      </Flex>
    </Box>
  </Box>
);

export default RosaPreviewCard;
